import React from 'react';
import { Link } from 'react-router-dom';
import { Wallet, ArrowLeftRight, TrendingUp, CreditCard, ArrowDownLeft, ArrowUpRight, CheckCircle2, XCircle } from 'lucide-react';
import { usePlatform } from '../../context/PlatformContext';

export default function PlatformDashboard() {
  const { tenant, stats, loaded } = usePlatform();

  const w = stats?.wallets || {};
  const t = stats?.transactions || {};
  const v = stats?.volume || {};

  const cards = [
    { label: 'Total Wallets', value: w.total ?? '—', sub: `${w.active ?? 0} active`, icon: Wallet, color: 'bg-pink-50 text-[#E8678B]' },
    { label: 'With VA', value: w.with_va ?? '—', sub: `${w.without_va ?? 0} without`, icon: CreditCard, color: 'bg-purple-50 text-purple-600' },
    { label: 'Transactions', value: t.total ?? '—', sub: `${t.success_rate ?? 0}% success`, icon: ArrowLeftRight, color: 'bg-blue-50 text-blue-600' },
    { label: 'Total Balance', value: loaded ? `₦${Number(v.total_balance_ngn || 0).toLocaleString()}` : '—', sub: 'across all wallets', icon: TrendingUp, color: 'bg-green-50 text-green-600' },
  ];

  const volumeCards = [
    { label: 'Total Inflow', value: `₦${Number(v.total_inflow_ngn || 0).toLocaleString()}`, icon: ArrowDownLeft, iconColor: 'text-green-600' },
    { label: 'Total Outflow', value: `₦${Number(v.total_outflow_ngn || 0).toLocaleString()}`, icon: ArrowUpRight, iconColor: 'text-orange-600' },
    { label: 'Completed', value: t.completed ?? 0, icon: CheckCircle2, iconColor: 'text-green-600' },
    { label: 'Failed', value: t.failed ?? 0, icon: XCircle, iconColor: 'text-red-500' },
  ];

  return (
    <div data-testid="platform-dashboard-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Welcome back, {tenant.name || 'there'}
        </h1>
        <p className="text-gray-500 mt-1">Here's how your platform is doing</p>
      </div>

      {tenant.status === 'sandbox' && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center justify-between" data-testid="sandbox-banner">
          <div>
            <p className="text-sm font-medium text-yellow-800">You're in sandbox mode</p>
            <p className="text-sm text-yellow-600 mt-0.5">All data is test data. Complete verification to go live with real banking.</p>
          </div>
          <Link to="/platform/go-live" className="text-sm font-semibold text-[#E8678B] hover:underline whitespace-nowrap">
            Go Live &rarr;
          </Link>
        </div>
      )}

      {/* Main stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map(card => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{card.label}</span>
              <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center`}>
                <card.icon size={16} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{loaded ? card.value : '—'}</p>
            <p className="text-xs text-gray-400 mt-1">{loaded ? card.sub : ''}</p>
          </div>
        ))}
      </div>

      {/* Volume breakdown */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {volumeCards.map(card => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3" data-testid={`volume-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 mb-1">
              <card.icon size={14} className={card.iconColor} />
              <p className="text-xs text-gray-500">{card.label}</p>
            </div>
            <p className="text-lg font-bold text-gray-900">{loaded ? card.value : '—'}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/platform/get-started" className="p-4 border border-gray-200 rounded-lg hover:border-[#E8678B] hover:bg-pink-50/30 transition-colors" data-testid="quick-action-get-started">
            <p className="text-sm font-semibold text-gray-900">View Quick Start Guide</p>
            <p className="text-xs text-gray-500 mt-1">Code snippets to get started in 5 minutes</p>
          </Link>
          <Link to="/platform/api-keys" className="p-4 border border-gray-200 rounded-lg hover:border-[#E8678B] hover:bg-pink-50/30 transition-colors" data-testid="quick-action-api-keys">
            <p className="text-sm font-semibold text-gray-900">Manage API Keys</p>
            <p className="text-xs text-gray-500 mt-1">View your test and live credentials</p>
          </Link>
          <Link to="/platform/wallets" className="p-4 border border-gray-200 rounded-lg hover:border-[#E8678B] hover:bg-pink-50/30 transition-colors" data-testid="quick-action-wallets">
            <p className="text-sm font-semibold text-gray-900">View Wallets</p>
            <p className="text-xs text-gray-500 mt-1">See all wallets created through your API</p>
          </Link>
          <Link to="/platform/customize" className="p-4 border border-gray-200 rounded-lg hover:border-[#E8678B] hover:bg-pink-50/30 transition-colors" data-testid="quick-action-customize">
            <p className="text-sm font-semibold text-gray-900">Customize Wallet UI</p>
            <p className="text-xs text-gray-500 mt-1">Configure how wallets look in your app</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
